import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  WHCustomerDOM,
  WHHttpParams,
  WHWorkerDOM,
} from '@workheld/workheld-shared-lib';
import {
  WorkObjectNewDOM,
  WorkObjectsDataService,
} from 'src/app/app-services/work-objects-data.service';
import { ErrorHandlerService } from 'src/app/app-services-async/w-h-error-handler.service';
import { catchError, forkJoin, of } from 'rxjs';

interface BoardParams extends WHHttpParams {
  targetState?: string;
  incoming?: boolean;
}

interface NumberPerStatus {
  id: number;
  count: number;
}

interface SharedFilters {
  customerId?: string;
  workerId?: string;
  targetState?: string;
  teamId?: string;
  term?: string;
}

interface SharedFilterFormValues {
  customerId?: WHCustomerDOM | { id?: string; name?: string } | null;
  workerId?: WHWorkerDOM | { id?: string; name?: string } | null;
  targetState?: { id?: string | number; translateKey?: string } | null;
}

interface RegisteredContext {
  contextId: number;
  calculatedStatusIds: number[];
  isSubcontractor: boolean;
}

type BoardState = {
  data: Map<string, WorkObjectNewDOM>;
  totalNumberPerStatus: Map<string, NumberPerStatus>;
  isLoading: Map<string, boolean>;
  requestVersion: number;
  params: Map<number, BoardParams>;
  scrollIndex: Map<number, number>;
  selectedTabIndex: number;
  activeContextId: number | null;
  sharedFilters: SharedFilters;
  sharedFilterFormValues: SharedFilterFormValues;
  registeredContexts: Map<number, RegisteredContext>;
  staleContexts: Set<number>;
};

const initialState: BoardState = {
  data: new Map(),
  totalNumberPerStatus: new Map(),
  isLoading: new Map(),
  requestVersion: 0,
  params: new Map(),
  scrollIndex: new Map(),
  selectedTabIndex: 0,
  activeContextId: null,
  sharedFilters: {},
  sharedFilterFormValues: {},
  registeredContexts: new Map(),
  staleContexts: new Set(),
};

const SUBCONTRACTOR_STATUS_ID = 9;

function buildSubcontractorParams(
  isSubcontractor: boolean,
  calculatedStatusId: number,
): { incoming: boolean | null; calculatedStatusId: number | null } {
  const isIncoming =
    isSubcontractor && calculatedStatusId === SUBCONTRACTOR_STATUS_ID;
  return {
    incoming: isIncoming || null,
    calculatedStatusId: isIncoming ? null : calculatedStatusId,
  };
}

export const BoardStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    totalElementsPerContext: computed(() => {
      const result = new Map<number, number>();
      store.totalNumberPerStatus().forEach((value, key) => {
        const contextId = parseInt(key.split('-')[0], 10);
        result.set(contextId, (result.get(contextId) ?? 0) + value.count);
      });
      return result;
    }),
    isAnyLoading: computed(() =>
      Array.from(store.isLoading().values()).some((v) => v),
    ),
  })),
  withMethods((store, workObjectService = inject(WorkObjectsDataService)) => ({
    registerContext(context: RegisteredContext) {
      patchState(store, (state) => ({
        registeredContexts: new Map(state.registeredContexts).set(
          context.contextId,
          context,
        ),
      }));
    },
    setParams(contextId: number, params: BoardParams) {
      patchState(store, (state) => ({
        params: new Map(state.params).set(contextId, params),
      }));
    },
    setSharedFilters(filters: SharedFilters) {
      const current = store.sharedFilters();
      if (
        current.customerId === filters.customerId &&
        current.workerId === filters.workerId &&
        current.targetState === filters.targetState &&
        current.teamId === filters.teamId &&
        current.term === filters.term
      ) {
        return;
      }

      const requestVersion = store.requestVersion() + 1;

      patchState(store, (state) => {
        const newParams = new Map(state.params);
        state.registeredContexts.forEach((_, ctxId) => {
          newParams.set(ctxId, {
            ...(newParams.get(ctxId) ?? {}),
            customerId: filters.customerId,
            workerId: filters.workerId,
            targetState: filters.targetState,
            teamId: filters.teamId,
            term: filters.term,
            page: 0,
          });
        });

        const newTotalNumberPerStatus = new Map(state.totalNumberPerStatus);
        const activeContext =
          state.activeContextId != null
            ? state.registeredContexts.get(state.activeContextId)
            : null;
        activeContext?.calculatedStatusIds.forEach((statusId) => {
          newTotalNumberPerStatus.delete(
            `${state.activeContextId}-${statusId}`,
          );
        });

        return {
          requestVersion,
          sharedFilters: filters,
          params: newParams,
          data: new Map(),
          totalNumberPerStatus: newTotalNumberPerStatus,
          scrollIndex: new Map(),
        };
      });

      const activeContextId = store.activeContextId();
      store.registeredContexts().forEach((context) => {
        if (context.contextId === activeContextId) {
          return;
        }
        this.loadBoardCounts({
          contextId: context.contextId,
          calculatedStatusIds: context.calculatedStatusIds,
          params: store.params().get(context.contextId) ?? {},
          isSubcontractor: context.isSubcontractor,
          requestVersion,
        });
      });
    },
    setSharedFilterFormValues(formValues: SharedFilterFormValues) {
      patchState(store, { sharedFilterFormValues: formValues });
    },
    invalidateContext(contextId: number) {
      patchState(store, (state) => {
        const newData = new Map(state.data);
        const context = state.registeredContexts.get(contextId);
        context?.calculatedStatusIds.forEach((statusId) => {
          newData.delete(`${contextId}-${statusId}`);
        });

        return {
          data: newData,
          params: new Map(state.params).set(contextId, {
            ...state.params.get(contextId),
            page: 0,
          }),
          scrollIndex: new Map(state.scrollIndex).set(contextId, 0),
        };
      });
    },
    markContextStale(contextId: number) {
      patchState(store, (state) => ({
        staleContexts: new Set(state.staleContexts).add(contextId),
        params: new Map(state.params).set(contextId, {
          ...state.params.get(contextId),
          page: 0,
        }),
      }));
    },
    isContextStale(contextId: number): boolean {
      return store.staleContexts().has(contextId);
    },
    setTeamId(teamId: string) {
      this.setSharedFilters({ ...store.sharedFilters(), teamId });
    },
    getSearchTerm(contextId: number) {
      return store.params().get(contextId)?.term ?? '';
    },
    getPage(contextId: number) {
      return store.params().get(contextId)?.page ?? 0;
    },
    setPage(contextId: number, page: number) {
      patchState(store, (state) => ({
        params: new Map(state.params).set(contextId, {
          ...(state.params.get(contextId) ?? {}),
          page,
        }),
      }));
    },
    getScrollIndex(contextId: number) {
      return store.scrollIndex().get(contextId) ?? 0;
    },
    setScrollIndex(contextId: number, scrollIndex: number) {
      patchState(store, (state) => ({
        scrollIndex: new Map(state.scrollIndex).set(contextId, scrollIndex),
      }));
    },
    getIsAnyLoading() {
      return Array.from(store.isLoading().values()).some((v) => v);
    },
    getIsLoading(contextId: number, calculatedStatusId: number) {
      return (
        store.isLoading().get(`${contextId}-${calculatedStatusId}`) ?? true
      );
    },
    hasMoreData(contextId: number): boolean {
      const context = store.registeredContexts().get(contextId);
      if (!context) return false;
      return context.calculatedStatusIds.some((statusId) => {
        const entry = store.data().get(`${contextId}-${statusId}`);
        return !entry || entry.content?.length < entry.totalElements;
      });
    },
    setSelectedTabIndex(tabIndex: number) {
      patchState(store, { selectedTabIndex: tabIndex });
    },
    setActiveContextId(contextId: number | null) {
      patchState(store, { activeContextId: contextId });
    },
    loadBoard({
      contextId,
      calculatedStatusId,
      params,
      isSubcontractor,
      requestVersion = store.requestVersion(),
    }: {
      contextId: number;
      calculatedStatusId: number;
      params: WHHttpParams;
      isSubcontractor: boolean;
      requestVersion?: number;
    }) {
      const key = `${contextId}-${calculatedStatusId}`;
      patchState(store, (state) => ({
        isLoading: new Map(state.isLoading).set(key, true),
      }));

      workObjectService
        .getWorkObjects(
          {
            ...params,
            ...buildSubcontractorParams(isSubcontractor, calculatedStatusId),
            includeCountDocuments: true,
            sort: 'modifiedDate,desc',
            countDefect: true,
            includeEquipment: true,
          } as any,
          contextId,
        )
        .subscribe({
          next: (workObjectPreviewDTO) => {
            if (store.requestVersion() === requestVersion) {
              patchState(store, (state) => {
                const existing = state.data.get(key);

                if (!existing || workObjectPreviewDTO?.number === 0) {
                  const newStaleContexts = new Set(state.staleContexts);
                  newStaleContexts.delete(contextId);
                  return {
                    data: new Map(state.data).set(key, {
                      ...workObjectPreviewDTO,
                      number: workObjectPreviewDTO?.number,
                    }),
                    totalNumberPerStatus: new Map(
                      state.totalNumberPerStatus,
                    ).set(key, {
                      id: calculatedStatusId,
                      count: workObjectPreviewDTO.totalElements,
                    }),
                    staleContexts: newStaleContexts,
                  };
                }

                if (params?.page > (existing.number ?? 0)) {
                  return {
                    data: new Map(state.data).set(key, {
                      ...workObjectPreviewDTO,
                      content: [
                        ...existing.content,
                        ...workObjectPreviewDTO.content,
                      ],
                      number: params?.page,
                    }),
                  };
                }

                return {};
              });
            }

            patchState(store, (state) => ({
              isLoading: new Map(state.isLoading).set(key, false),
            }));
          },
          error: (err) => {
            patchState(store, (state) => ({
              isLoading: new Map(state.isLoading).set(key, false),
            }));
            ErrorHandlerService.handleGlobalError(err);
          },
        });
    },
    loadBoardCounts({
      contextId,
      calculatedStatusIds,
      params,
      isSubcontractor,
      requestVersion = store.requestVersion(),
    }: {
      contextId: number;
      calculatedStatusIds: number[];
      params: WHHttpParams;
      isSubcontractor: boolean;
      requestVersion?: number;
    }) {
      const requests = calculatedStatusIds.map((calculatedStatusId) =>
        workObjectService
          .getWorkObjects(
            {
              ...params,
              size: 1,
              page: 0,
              ...buildSubcontractorParams(isSubcontractor, calculatedStatusId),
            } as any,
            contextId,
          )
          .pipe(catchError(() => of(null))),
      );

      forkJoin(requests).subscribe((results) => {
        if (store.requestVersion() !== requestVersion) {
          return;
        }

        patchState(store, (state) => {
          const newTotalNumberPerStatus = new Map(state.totalNumberPerStatus);
          results.forEach((workObjectPreviewDTO, index) => {
            const statusId = calculatedStatusIds[index];
            newTotalNumberPerStatus.set(`${contextId}-${statusId}`, {
              id: statusId,
              count: workObjectPreviewDTO?.totalElements ?? 0,
            });
          });
          return { totalNumberPerStatus: newTotalNumberPerStatus };
        });
      });
    },
  })),
);
